import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Camera, Image as ImageIcon, Sparkle, MagicWand, PaintBrush, Export, Upload, Trash, ShieldCheck, Tag, Lightning, Flame, CreditCard, Gauge as GaugeIcon, UserCircle, Star, ArrowDown, ArrowsOut, X, CaretRight, CheckCircle, Spinner, DownloadSimple, Copy, InstagramLogo, FacebookLogo, TiktokLogo, Globe, ImageSquare, CubeTransparent, FilmStrip, Microphone } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../utils/api';
import BeforeAfterSlider from './BeforeAfterSlider';
import Viewer360 from './Viewer360';
import { useLanguage } from '../context/LanguageContext';

const BADGE_ICONS = { 'shield-check': ShieldCheck, 'flame': Flame, 'credit-card': CreditCard, 'award': Star, 'gauge': GaugeIcon, 'user': UserCircle, 'sparkles': Sparkle, 'trending-down': ArrowDown };
const SOCIAL_ICONS = { instagram_square: InstagramLogo, instagram_story: InstagramLogo, facebook_marketplace: FacebookLogo, tiktok_vertical: TiktokLogo, website_banner: Globe, thumbnail: ImageSquare };

export default function DealerImagePro() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('background');
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [resultImage, setResultImage] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [backgrounds, setBackgrounds] = useState([]);
  const [badges, setBadges] = useState([]);
  const [socialFormats, setSocialFormats] = useState({});
  const [mediaLibrary, setMediaLibrary] = useState([]);
  const [selectedBg, setSelectedBg] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState('instagram_square');
  const [vehicleInfo, setVehicleInfo] = useState('');
  const [captions, setCaptions] = useState(null);
  const [enhanceMode, setEnhanceMode] = useState('all');
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);

  // Plan & usage state
  const [myPlan, setMyPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [showPlanModal, setShowPlanModal] = useState(false);

  // Badge editor state
  const [placedBadges, setPlacedBadges] = useState([]);
  const [draggingBadge, setDraggingBadge] = useState(null);
  const [priceOverlay, setPriceOverlay] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [videoPrompt, setVideoPrompt] = useState('');
  const [videoDuration, setVideoDuration] = useState(4);
  const [videoResult, setVideoResult] = useState(null);
  const [showBeforeAfter, setShowBeforeAfter] = useState(false);
  const [activeView, setActiveView] = useState('editor'); // 'editor' | '360'
  const [customBgPrompt, setCustomBgPrompt] = useState('');
  const customBgRef = useRef(null);
  const [showSharePanel, setShowSharePanel] = useState(false);

  // Reel state
  const [reelStyles, setReelStyles] = useState([]);
  const [selectedReelStyle, setSelectedReelStyle] = useState('cinematic_pan');
  const [reelVehicle, setReelVehicle] = useState('');
  const [reelOrientation, setReelOrientation] = useState('vertical');
  const [reelDuration, setReelDuration] = useState(4);
  const [reelResult, setReelResult] = useState(null);

  // Voice-over state
  const [voiceOptions, setVoiceOptions] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState('onyx');
  const [voScript, setVoScript] = useState('');
  const [voVehicle, setVoVehicle] = useState('');
  const [voDealerName, setVoDealerName] = useState('');
  const [voFeatures, setVoFeatures] = useState('');
  const [voPrice, setVoPrice] = useState('');
  const [voTemplate, setVoTemplate] = useState('dealership');
  const [audioResult, setAudioResult] = useState(null);

  const fetchMyPlan = useCallback(async () => {
    try {
      const { data } = await api.get('/api/image-pro/my-plan');
      setMyPlan(data);
    } catch {}
  }, []);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const [bgRes, badgeRes, fmtRes, libRes, planRes, reelRes, voiceRes] = await Promise.all([
          api.get('/api/image-pro/backgrounds'),
          api.get('/api/image-pro/badges'),
          api.get('/api/image-pro/social-formats'),
          api.get('/api/image-pro/library?limit=30'),
          api.get('/api/image-pro/plans'),
          api.get('/api/image-pro/reel-styles'),
          api.get('/api/image-pro/voice-options'),
        ]);
        setBackgrounds(bgRes.data);
        setBadges(badgeRes.data);
        setSocialFormats(fmtRes.data);
        setMediaLibrary(libRes.data);
        setPlans(planRes.data);
        setReelStyles(reelRes.data);
        setVoiceOptions(voiceRes.data);
      } catch {}
    };
    fetchConfig();
    fetchMyPlan();
  }, [fetchMyPlan]);

  const handleFileSelect = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Universal format support — convert non-standard formats to JPEG via Canvas
    const standardTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    let processedFile = file;
    if (!standardTypes.includes(file.type)) {
      try {
        processedFile = await new Promise((resolve) => {
          const img = new Image();
          const url = URL.createObjectURL(file);
          img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            canvas.getContext('2d').drawImage(img, 0, 0);
            canvas.toBlob((blob) => {
              URL.revokeObjectURL(url);
              resolve(blob ? new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }) : file);
            }, 'image/jpeg', 0.95);
          };
          img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
          img.src = url;
        });
      } catch { processedFile = file; }
    }
    setSelectedFile(processedFile);
    setResultImage(null);
    setCaptions(null);
    const reader = new FileReader();
    reader.onload = (ev) => setSelectedImage(ev.target.result);
    reader.readAsDataURL(processedFile);
  }, []);

  // Drag-and-drop handler
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer?.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const fakeEvent = { target: { files: [file] } };
      handleFileSelect(fakeEvent);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e) => { e.preventDefault(); e.stopPropagation(); }, []);

  const handleUploadToLibrary = async () => {
    if (!selectedFile) return;
    const formData = new FormData();
    formData.append('file', selectedFile);
    try {
      await api.post('/api/image-pro/upload', formData);
      toast.success('Saved to media library');
      const { data } = await api.get('/api/image-pro/library?limit=30');
      setMediaLibrary(data);
    } catch { toast.error('Upload failed'); }
  };

  const handleBackgroundReplace = async () => {
    if (!selectedFile) return toast.error('Select an image first');
    setProcessing(true);
    setResultImage(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (selectedBg) formData.append('preset_id', selectedBg);
      if (customBgPrompt && !customBgPrompt.startsWith('[UPLOADED_BG:')) formData.append('custom_prompt', customBgPrompt);
      const { data } = await api.post('/api/image-pro/background-replace', formData, { timeout: 120000 });
      setResultImage(`data:image/png;base64,${data.image_base64}`);
      toast.success('Background replaced!');
    } catch (err) {
      const detail = err.response?.data?.detail || 'AI processing failed';
      if (err.response?.status === 403 || err.response?.status === 429) {
        toast.error(detail);
        setShowPlanModal(true);
      } else {
        toast.error(detail);
      }
    }
    finally { setProcessing(false); }
  };

  const handleEnhance = async () => {
    if (!selectedFile) return toast.error('Select an image first');
    setProcessing(true);
    setResultImage(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('enhancements', enhanceMode);
      const { data } = await api.post('/api/image-pro/enhance', formData, { timeout: 120000 });
      setResultImage(`data:image/png;base64,${data.image_base64}`);
      toast.success('Photo enhanced!');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Enhancement failed';
      if (err.response?.status === 403 || err.response?.status === 429) {
        toast.error(detail);
        setShowPlanModal(true);
      } else {
        toast.error(detail);
      }
    }
    finally { setProcessing(false); }
  };

  const handleGenerateListing = async () => {
    if (!selectedFile) return toast.error('Select an image first');
    setProcessing(true);
    setResultImage(null);
    setCaptions(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('format', selectedFormat);
      formData.append('vehicle_info', vehicleInfo);
      const { data } = await api.post('/api/image-pro/generate-listing', formData, { timeout: 120000 });
      setResultImage(`data:image/png;base64,${data.image_base64}`);
      setCaptions(data.captions);
      toast.success('Listing generated!');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Generation failed';
      if (err.response?.status === 403 || err.response?.status === 429) {
        toast.error(detail);
        setShowPlanModal(true);
      } else {
        toast.error(detail);
      }
    }
    finally { setProcessing(false); }
  };

  const handleDownloadResult = () => {
    if (!resultImage) return;
    const a = document.createElement('a');
    a.href = resultImage;
    a.download = `wakzy_image_pro_${Date.now()}.png`;
    a.click();
  };

  const handleSubscribePlan = async (planId) => {
    try {
      const { data } = await api.post('/api/image-pro/subscribe', {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.success('Image Pro plan activated!');
        setShowPlanModal(false);
        fetchMyPlan();
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Subscription failed'); }
  };

  // Handle return from Stripe checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const planId = params.get('plan');
    if (params.get('image_pro_success') === 'true' && planId) {
      api.post('/api/image-pro/activate', { plan_id: planId })
        .then(() => { toast.success('Image Pro plan activated!'); fetchMyPlan(); })
        .catch(() => {});
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [fetchMyPlan]);

  const handleCancelPlan = async () => {
    try {
      await api.delete('/api/image-pro/subscribe');
      toast.success('Image Pro cancelled');
      fetchMyPlan();
    } catch { toast.error('Failed to cancel'); }
  };

  const handleDamageDetect = async () => {
    if (!selectedFile) return toast.error('Select an image first');
    setProcessing(true);
    setResultImage(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const { data } = await api.post('/api/image-pro/damage-detect', formData, { timeout: 120000 });
      setResultImage(`data:image/png;base64,${data.image_base64}`);
      toast.success('Damage analysis complete!');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Damage detection failed';
      if (err.response?.status === 403 || err.response?.status === 429) { toast.error(detail); setShowPlanModal(true); }
      else toast.error(detail);
    }
    finally { setProcessing(false); }
  };

  const handleGenerateVideo = async () => {
    if (!videoPrompt) return toast.error('Enter a video description');
    setProcessing(true);
    setVideoResult(null);
    try {
      const { data } = await api.post('/api/image-pro/generate-video', {
        prompt: videoPrompt, duration: videoDuration, size: '1280x720'
      }, { timeout: 660000 });
      setVideoResult(`data:video/mp4;base64,${data.video_base64}`);
      toast.success('Video generated!');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Video generation failed';
      if (err.response?.status === 403 || err.response?.status === 429) { toast.error(detail); setShowPlanModal(true); }
      else toast.error(detail);
    }
    finally { setProcessing(false); }
  };

  const handleGenerateReel = async () => {
    if (!reelVehicle) return toast.error('Enter a vehicle description');
    setProcessing(true);
    setReelResult(null);
    try {
      const { data } = await api.post('/api/image-pro/generate-reel', {
        vehicle_description: reelVehicle, style: selectedReelStyle,
        orientation: reelOrientation, duration: reelDuration,
      }, { timeout: 660000 });
      setReelResult(`data:video/mp4;base64,${data.video_base64}`);
      toast.success(`${data.style} reel generated!`);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Reel generation failed';
      if (err.response?.status === 403 || err.response?.status === 429) { toast.error(detail); setShowPlanModal(true); }
      else toast.error(detail);
    }
    finally { setProcessing(false); }
  };

  const handleGenerateScript = async () => {
    try {
      const { data } = await api.post('/api/image-pro/generate-script', {
        template: voTemplate, vehicle: voVehicle, dealer_name: voDealerName,
        features: voFeatures, price: voPrice,
      });
      setVoScript(data.script);
      toast.success('Script generated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Script generation failed'); }
  };

  const handleGenerateVoiceover = async () => {
    if (!voScript) return toast.error('Generate or enter a script first');
    setProcessing(true);
    setAudioResult(null);
    try {
      const { data } = await api.post('/api/image-pro/generate-voiceover', {
        script: voScript, voice: selectedVoice, speed: 1.0, quality: 'hd',
      }, { timeout: 120000 });
      setAudioResult(`data:audio/mp3;base64,${data.audio_base64}`);
      toast.success(`Voice-over generated (${Math.round(data.duration_estimate)}s)`);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Voice-over failed';
      if (err.response?.status === 403 || err.response?.status === 429) { toast.error(detail); setShowPlanModal(true); }
      else toast.error(detail);
    }
    finally { setProcessing(false); }
  };

  const handleDownloadBranded = useCallback(() => {
    if (!selectedImage || placedBadges.length === 0) return;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      // Draw badges
      placedBadges.forEach(badge => {
        const bx = (badge.x / 100) * canvas.width;
        const by = (badge.y / 100) * canvas.height;
        const fontSize = Math.max(14, canvas.width * 0.022);
        ctx.font = `bold ${fontSize}px Manrope, sans-serif`;
        const textWidth = ctx.measureText(badge.text).width;
        const padX = fontSize * 0.6, padY = fontSize * 0.4;
        // Background
        ctx.fillStyle = badge.color;
        const rh = fontSize + padY * 2;
        const rw = textWidth + padX * 2;
        const rx = bx, ry = by;
        ctx.beginPath();
        ctx.roundRect(rx, ry, rw, rh, 6);
        ctx.fill();
        // Text
        ctx.fillStyle = '#FFFFFF';
        ctx.fillText(badge.text, rx + padX, ry + padY + fontSize * 0.85);
      });
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = `wakzy_branded_${Date.now()}.png`;
      a.click();
      toast.success('Branded image downloaded!');
    };
    img.src = selectedImage;
  }, [selectedImage, placedBadges]);

  const handleDeleteMedia = async (id) => {
    try {
      await api.delete(`/api/image-pro/library/${id}`);
      setMediaLibrary(prev => prev.filter(m => m.id !== id));
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  const TABS = [
    { id: 'background', label: 'BG Swap', icon: MagicWand },
    { id: 'enhance', label: 'Enhance', icon: Sparkle },
    { id: 'branding', label: 'Brand', icon: Tag },
    { id: 'listing', label: 'Listing', icon: Export },
    { id: 'damage', label: 'Damage', icon: ShieldCheck },
    { id: 'video', label: 'Video', icon: Lightning },
    { id: 'reels', label: 'Reels', icon: FilmStrip },
    { id: 'voiceover', label: 'Voice', icon: Microphone },
    { id: '360', label: '360°', icon: CubeTransparent },
  ];

  const ENHANCE_MODES = [
    { id: 'all', label: 'Full Auto', desc: 'All enhancements' },
    { id: 'paint', label: 'Paint Shine', desc: 'Gloss & reflection' },
    { id: 'color', label: 'Color Pop', desc: 'Vivid colors' },
    { id: 'detail', label: 'Detail+', desc: 'Sharpen everything' },
    { id: 'lighting', label: 'Lighting', desc: 'Pro exposure' },
  ];

  return (
    <div className="min-h-[calc(100vh-80px)] bg-[#050914] text-white" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="dealer-image-pro">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-slate-900/60 backdrop-blur-2xl backdrop-saturate-150 border-b border-white/5 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-[0_0_15px_rgba(0,240,255,0.3)]">
            <Camera weight="bold" className="w-5 h-5 text-black" />
          </div>
          <div>
            <h1 className="text-lg font-bold tracking-tight text-white">Dealer Image Pro</h1>
            <p className="text-[10px] tracking-[0.2em] uppercase text-cyan-400 font-bold">AI Photography Suite</p>
          </div>
          {myPlan?.plan_id ? (
            <span className="ml-3 text-[10px] tracking-wider uppercase font-bold px-2.5 py-1 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/30" data-testid="plan-badge">
              {myPlan.plan?.name} — {myPlan.limit === -1 ? 'Unlimited' : `${myPlan.remaining}/${myPlan.limit} left`}
            </span>
          ) : (
            <button onClick={() => setShowPlanModal(true)} className="ml-3 text-[10px] tracking-wider uppercase font-bold px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30 hover:bg-amber-500/20 transition-colors" data-testid="subscribe-prompt">
              Activate Add-on
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {resultImage && selectedImage && (
            <Button onClick={() => setShowBeforeAfter(prev => !prev)} variant="outline"
              className={`border-slate-700 rounded-xl text-sm h-9 px-4 ${showBeforeAfter ? 'text-cyan-400 border-cyan-500/50' : 'text-slate-300'}`}
              data-testid="toggle-compare-btn">
              {showBeforeAfter ? 'Exit Compare' : 'Compare Before/After'}
            </Button>
          )}
          {resultImage && (
            <>
              <Button onClick={handleDownloadResult} className="bg-cyan-500 text-black font-bold hover:bg-cyan-400 shadow-[0_0_15px_rgba(0,240,255,0.4)] rounded-xl text-sm h-9 px-4" data-testid="download-result-btn">
                <DownloadSimple weight="bold" className="w-4 h-4 mr-1.5" />{t('imagePro.download')}
              </Button>
              <Button onClick={() => setShowSharePanel(!showSharePanel)} variant="outline"
                className={`border-slate-700 rounded-xl text-sm h-9 px-4 ${showSharePanel ? 'text-cyan-400 border-cyan-500/50' : 'text-slate-300'}`}
                data-testid="share-btn">
                <Export weight="bold" className="w-4 h-4 mr-1.5" />{t('imagePro.share')}
              </Button>
              <Button onClick={handleUploadToLibrary} variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 rounded-xl text-sm h-9 px-4" data-testid="save-to-library-btn">
                {t('imagePro.saveToLibrary')}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="flex h-[calc(100vh-140px)]">
        {/* Left Sidebar — Media Library */}
        <div className="w-56 flex-shrink-0 bg-[#0A101D] border-r border-white/5 flex flex-col">
          <div className="p-4 border-b border-white/5">
            <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400 mb-3">Media Library</p>
            <Button onClick={() => fileInputRef.current?.click()} className="w-full bg-slate-800 text-white border border-slate-700 hover:bg-slate-700 rounded-xl text-xs h-8" data-testid="upload-btn">
              <Upload weight="bold" className="w-3.5 h-3.5 mr-1.5" />Upload Image
            </Button>
            <input ref={fileInputRef} type="file" accept="image/*,.heic,.heif,.bmp,.tiff,.tif,.avif,.raw,.cr2,.nef,.arw" className="hidden" onChange={handleFileSelect} data-testid="file-input" />
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            {mediaLibrary.length === 0 ? (
              <div className="text-center py-8 text-slate-500 text-xs">
                <ImageIcon weight="duotone" className="w-8 h-8 mx-auto mb-2 text-slate-600" />
                No images yet
              </div>
            ) : mediaLibrary.map(item => (
              <div key={item.id} className="group relative rounded-lg overflow-hidden border border-white/5 hover:border-cyan-500/50 transition-all cursor-pointer" data-testid={`media-${item.id}`}
                onClick={async () => {
                  try {
                    const { data } = await api.get(`/api/image-pro/library/${item.id}`);
                    setSelectedImage(data.data_url);
                    // Convert data URL to File for API calls
                    const res = await fetch(data.data_url);
                    const blob = await res.blob();
                    setSelectedFile(new File([blob], data.filename, { type: blob.type }));
                    setResultImage(null);
                    setCaptions(null);
                  } catch {}
                }}
              >
                <div className="aspect-video bg-slate-800 flex items-center justify-center">
                  <ImageIcon weight="duotone" className="w-6 h-6 text-slate-600" />
                </div>
                <div className="p-1.5">
                  <p className="text-[10px] text-slate-400 truncate">{item.filename}</p>
                </div>
                <button onClick={(e) => { e.stopPropagation(); handleDeleteMedia(item.id); }}
                  className="absolute top-1 right-1 bg-red-500/80 rounded p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-media-${item.id}`}>
                  <X weight="bold" className="w-3 h-3 text-white" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Main Canvas */}
        <div className="flex-1 flex items-center justify-center p-6 bg-[#050914] relative overflow-hidden">
          {/* Subtle grid pattern */}
          <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle, #00F0FF 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

          {processing && (
            <div className="absolute inset-0 z-20 bg-[#050914]/90 flex flex-col items-center justify-center" data-testid="processing-overlay">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-4 animate-pulse shadow-[0_0_30px_rgba(0,240,255,0.5)]">
                <Spinner weight="bold" className="w-8 h-8 text-black animate-spin" />
              </div>
              <p className="text-white font-semibold">AI Processing</p>
              <p className="text-sm text-slate-400 mt-1">This may take 30-60 seconds...</p>
            </div>
          )}

          <div className="relative z-10 max-w-3xl w-full">
            {/* 360° Viewer takes full canvas */}
            {activeTab === '360' ? (
              <Viewer360 />
            ) : showBeforeAfter && selectedImage && resultImage ? (
              <div className="space-y-3">
                <BeforeAfterSlider beforeSrc={selectedImage} afterSrc={resultImage} />
                <div className="flex justify-center gap-2">
                  <Button onClick={() => setShowBeforeAfter(false)} variant="outline" className="border-slate-700 text-slate-300 rounded-xl text-xs h-8 px-3" data-testid="hide-compare-btn">
                    Hide Comparison
                  </Button>
                  <Button onClick={handleDownloadResult} className="bg-cyan-500 text-black font-bold hover:bg-cyan-400 rounded-xl text-xs h-8 px-3" data-testid="download-from-compare">
                    <DownloadSimple weight="bold" className="w-3.5 h-3.5 mr-1" />Download Result
                  </Button>
                </div>
              </div>
            ) : resultImage ? (
              <div className="space-y-4">
                <div className="rounded-xl border border-white/10 overflow-hidden shadow-2xl">
                  <img src={resultImage} alt="AI Result" className="w-full object-contain max-h-[60vh]" data-testid="result-image" />
                </div>
                {/* Share Panel */}
                {showSharePanel && (
                  <div className="bg-[#0A101D] rounded-xl border border-white/5 p-4" data-testid="share-panel">
                    <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-cyan-400 mb-3">{t('imagePro.sharePost')}</p>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { name: 'Facebook', icon: FacebookLogo, color: '#1877F2', action: () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank') },
                        { name: 'Instagram', icon: InstagramLogo, color: '#E4405F', action: () => { handleDownloadResult(); toast.success('Image downloaded — open Instagram to post'); } },
                        { name: 'TikTok', icon: TiktokLogo, color: '#000000', action: () => { handleDownloadResult(); toast.success('Image downloaded — open TikTok to post'); } },
                        { name: 'X / Twitter', icon: Globe, color: '#1DA1F2', action: () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent('Check out this vehicle! ' + (vehicleInfo || '') + ' #WakzyAutoTech #CarsForSale')}&url=${encodeURIComponent(window.location.href)}`, '_blank') },
                        { name: 'WhatsApp', icon: Globe, color: '#25D366', action: () => window.open(`https://wa.me/?text=${encodeURIComponent('Check out this vehicle! ' + (vehicleInfo || ''))}`, '_blank') },
                        { name: 'Copy Image', icon: Copy, color: '#64748B', action: async () => {
                          try {
                            const res = await fetch(resultImage);
                            const blob = await res.blob();
                            await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
                            toast.success('Image copied to clipboard');
                          } catch { toast.error('Copy failed — try downloading instead'); }
                        }},
                      ].map(s => (
                        <button key={s.name} onClick={s.action}
                          className="flex items-center gap-2 p-2.5 rounded-xl border border-slate-700 bg-slate-800/50 hover:border-cyan-500/30 transition-all text-left"
                          data-testid={`share-${s.name.toLowerCase().replace(/[\s\/]/g,'-')}`}>
                          <s.icon weight="bold" className="w-4 h-4 flex-shrink-0" style={{ color: s.color }} />
                          <span className="text-[10px] font-semibold text-slate-300">{s.name}</span>
                        </button>
                      ))}
                    </div>
                    {captions?.caption && (
                      <div className="mt-3 pt-3 border-t border-white/5">
                        <p className="text-[10px] font-bold text-slate-400 mb-1">Caption for posting:</p>
                        <p className="text-xs text-slate-300 bg-slate-800 rounded-lg p-2">{captions.caption}</p>
                        <Button variant="ghost" size="sm" className="mt-1 text-xs text-slate-400 hover:text-white" onClick={() => { navigator.clipboard.writeText(captions.caption); toast.success('Caption copied!'); }}>
                          <Copy weight="bold" className="w-3 h-3 mr-1" />Copy Caption
                        </Button>
                      </div>
                    )}
                    <div className="mt-3 pt-3 border-t border-white/5">
                      <p className="text-[10px] font-bold text-slate-400 mb-1">{t('imagePro.websiteEmbed')}</p>
                      <div className="bg-slate-800 rounded-lg p-2 text-[10px] font-mono text-slate-400 break-all select-all cursor-text" data-testid="embed-code">
                        {`<img src="[your-image-url]" alt="Vehicle Photo by Wakzy Auto Tech" style="max-width:100%;border-radius:8px" />`}
                      </div>
                    </div>
                  </div>
                )}
                {captions && (
                  <div className="bg-[#0A101D] rounded-xl border border-white/5 p-4">
                    <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-cyan-400 mb-2">Generated Caption</p>
                    <p className="text-sm text-slate-300 whitespace-pre-line">{captions.caption}</p>
                    <Button variant="ghost" size="sm" className="mt-2 text-xs text-slate-400 hover:text-white" onClick={() => { navigator.clipboard.writeText(captions.caption); toast.success('Copied!'); }} data-testid="copy-caption-btn">
                      <Copy weight="bold" className="w-3 h-3 mr-1" />Copy Caption
                    </Button>
                  </div>
                )}
              </div>
            ) : selectedImage ? (
              <div className="rounded-xl border border-white/10 overflow-hidden shadow-2xl relative" ref={canvasRef}>
                <img src={selectedImage} alt="Selected" className="w-full object-contain max-h-[60vh]" data-testid="selected-image" />
                {/* Badge overlays */}
                {activeTab === 'branding' && placedBadges.map((badge, i) => (
                  <div key={badge.id}
                    className="absolute cursor-move select-none"
                    style={{ left: `${badge.x}%`, top: `${badge.y}%`, zIndex: 10 + i }}
                    draggable
                    onDragStart={(e) => {
                      setDraggingBadge(i);
                      e.dataTransfer.setDragImage(new window.Image(), 0, 0);
                    }}
                    onDrag={(e) => {
                      if (!canvasRef.current || !e.clientX) return;
                      const rect = canvasRef.current.getBoundingClientRect();
                      const x = ((e.clientX - rect.left) / rect.width) * 100;
                      const y = ((e.clientY - rect.top) / rect.height) * 100;
                      if (x > 0 && x < 95 && y > 0 && y < 95) {
                        setPlacedBadges(prev => prev.map((b, idx) => idx === i ? { ...b, x, y } : b));
                      }
                    }}
                    onDragEnd={() => setDraggingBadge(null)}
                    data-testid={`placed-badge-${i}`}
                  >
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold text-white shadow-lg" style={{ backgroundColor: badge.color }}>
                      {badge.text}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20 border-2 border-dashed border-slate-700 rounded-2xl cursor-pointer hover:border-cyan-500/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop} onDragOver={handleDragOver}
                data-testid="empty-canvas">
                <Camera weight="duotone" className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-xl font-bold text-slate-400">{t('imagePro.dropOrClick')}</p>
                <p className="text-sm text-slate-500 mt-2">{t('imagePro.allFormats')}</p>
                <p className="text-xs text-slate-600 mt-1">{t('imagePro.autoConvert')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar — AI Tools */}
        <div className="w-72 flex-shrink-0 bg-slate-900/60 backdrop-blur-2xl border-l border-white/10 flex flex-col">
          {/* Tool Tabs */}
          <div className="flex overflow-x-auto border-b border-white/5 scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex-shrink-0 py-3 px-3 text-center text-[10px] font-bold transition-all ${activeTab === tab.id ? 'text-white border-b-2 border-cyan-500 bg-slate-800/50' : 'text-slate-500 hover:text-slate-300'}`}
                data-testid={`tool-tab-${tab.id}`}>
                <tab.icon weight={activeTab === tab.id ? 'fill' : 'regular'} className="w-4 h-4 mx-auto mb-0.5" />
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* AI BACKGROUND REPLACE */}
            {activeTab === 'background' && (
              <>
                <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400">{t('imagePro.bgPresets')}</p>
                <div className="grid grid-cols-2 gap-2">
                  {backgrounds.map(bg => (
                    <button key={bg.id} onClick={() => { setSelectedBg(bg.id); setCustomBgPrompt(''); }}
                      className={`p-3 rounded-xl text-left text-xs border transition-all ${selectedBg === bg.id && !customBgPrompt ? 'border-cyan-500 bg-cyan-500/10 text-white' : 'border-slate-700 bg-slate-800/50 text-slate-400 hover:border-slate-500'}`}
                      data-testid={`bg-preset-${bg.id}`}>
                      <ArrowsOut weight="bold" className="w-4 h-4 mb-1" />
                      {bg.name}
                    </button>
                  ))}
                </div>

                <div className="border-t border-white/5 pt-3">
                  <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-cyan-400 mb-2">{t('imagePro.customBg')}</p>
                  <textarea value={customBgPrompt} onChange={e => { setCustomBgPrompt(e.target.value); if (e.target.value) setSelectedBg(null); }}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl p-2.5 text-xs text-white placeholder-slate-500 resize-none focus:border-cyan-500 focus:outline-none"
                    rows={2} placeholder="Describe your background: e.g. 'Mountain highway at sunset with dramatic clouds'" data-testid="custom-bg-prompt" />
                  <div className="flex gap-2 mt-2">
                    <input ref={customBgRef} type="file" accept="image/*" className="hidden" onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      const reader = new FileReader();
                      reader.onload = (ev) => { setCustomBgPrompt(`[UPLOADED_BG:${ev.target.result.substring(0,50)}]`); toast.success('Custom background ready'); };
                      reader.readAsDataURL(f);
                    }} data-testid="custom-bg-upload" />
                    <Button size="sm" variant="outline" onClick={() => customBgRef.current?.click()}
                      className="flex-1 text-[10px] border-slate-700 text-slate-300 rounded-lg h-7" data-testid="upload-bg-btn">
                      {t('imagePro.uploadBgImage')}
                    </Button>
                  </div>
                </div>

                <Button onClick={handleBackgroundReplace} disabled={processing || !selectedImage}
                  className="w-full bg-cyan-500 text-black font-bold hover:bg-cyan-400 shadow-[0_0_15px_rgba(0,240,255,0.4)] rounded-xl h-10 disabled:opacity-30"
                  data-testid="apply-bg-btn">
                  {processing ? <><Spinner weight="bold" className="w-4 h-4 mr-2 animate-spin" />{t('imagePro.processing')}</> : <><MagicWand weight="bold" className="w-4 h-4 mr-2" />{t('imagePro.replaceBg')}</>}
                </Button>
              </>
            )}

            {/* ENHANCE */}
            {activeTab === 'enhance' && (
              <>
                <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400">Enhancement Mode</p>
                <div className="space-y-2">
                  {ENHANCE_MODES.map(mode => (
                    <button key={mode.id} onClick={() => setEnhanceMode(mode.id)}
                      className={`w-full p-3 rounded-xl text-left border transition-all flex items-center justify-between ${enhanceMode === mode.id ? 'border-cyan-500 bg-cyan-500/10' : 'border-slate-700 bg-slate-800/50 hover:border-slate-500'}`}
                      data-testid={`enhance-mode-${mode.id}`}>
                      <div>
                        <p className={`text-sm font-semibold ${enhanceMode === mode.id ? 'text-white' : 'text-slate-300'}`}>{mode.label}</p>
                        <p className="text-[10px] text-slate-500">{mode.desc}</p>
                      </div>
                      {enhanceMode === mode.id && <CheckCircle weight="fill" className="w-5 h-5 text-cyan-400" />}
                    </button>
                  ))}
                </div>
                <Button onClick={handleEnhance} disabled={processing || !selectedImage}
                  className="w-full bg-cyan-500 text-black font-bold hover:bg-cyan-400 shadow-[0_0_15px_rgba(0,240,255,0.4)] rounded-xl h-10 disabled:opacity-30"
                  data-testid="apply-enhance-btn">
                  {processing ? <><Spinner weight="bold" className="w-4 h-4 mr-2 animate-spin" />Enhancing...</> : <><Sparkle weight="bold" className="w-4 h-4 mr-2" />Enhance Photo</>}
                </Button>
              </>
            )}

            {/* BRANDING */}
            {activeTab === 'branding' && (
              <>
                <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400">Click Badge to Place on Image</p>
                <div className="grid grid-cols-2 gap-2">
                  {badges.map(badge => {
                    const Icon = BADGE_ICONS[badge.icon] || Tag;
                    return (
                      <button key={badge.id} onClick={() => {
                        if (!selectedImage) return toast.error('Select an image first');
                        setPlacedBadges(prev => [...prev, { ...badge, x: 10, y: 10 + prev.length * 40, id: badge.id + '_' + Date.now() }]);
                      }}
                        className="flex items-center gap-2 p-2.5 rounded-xl border border-slate-700 bg-slate-800/50 text-xs hover:border-cyan-500/50 transition-all cursor-pointer text-left" data-testid={`badge-${badge.id}`}>
                        <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: badge.color + '20', color: badge.color }}>
                          <Icon weight="bold" className="w-3.5 h-3.5" />
                        </div>
                        <span className="text-slate-300 truncate text-[10px] font-semibold">{badge.text}</span>
                      </button>
                    );
                  })}
                </div>

                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400 mb-2">Price Overlay</p>
                  <input value={priceOverlay} onChange={e => setPriceOverlay(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl p-2.5 text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                    placeholder="$24,999" data-testid="price-overlay-input" />
                  {priceOverlay && (
                    <Button size="sm" variant="outline" className="mt-2 text-xs border-slate-700 text-slate-300 rounded-lg" onClick={() => {
                      if (!selectedImage) return toast.error('Select an image first');
                      setPlacedBadges(prev => [...prev, { id: 'price_' + Date.now(), text: priceOverlay, color: '#10B981', icon: 'credit-card', x: 10, y: 10 + prev.length * 40 }]);
                    }} data-testid="add-price-badge-btn">Add Price Badge</Button>
                  )}
                </div>

                {placedBadges.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400">Placed ({placedBadges.length})</p>
                      <button onClick={() => setPlacedBadges([])} className="text-[10px] text-red-400 hover:text-red-300" data-testid="clear-badges-btn">Clear All</button>
                    </div>
                    {placedBadges.map((b, i) => (
                      <div key={b.id} className="flex items-center justify-between text-xs text-slate-400 py-1">
                        <span className="truncate">{b.text}</span>
                        <button onClick={() => setPlacedBadges(prev => prev.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-300 ml-2">
                          <X weight="bold" className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    <Button onClick={handleDownloadBranded} className="w-full mt-3 bg-cyan-500 text-black font-bold hover:bg-cyan-400 shadow-[0_0_15px_rgba(0,240,255,0.4)] rounded-xl h-10" data-testid="download-branded-btn">
                      <DownloadSimple weight="bold" className="w-4 h-4 mr-2" />Download Branded Image
                    </Button>
                  </div>
                )}
              </>
            )}

            {/* LISTING GENERATOR */}
            {activeTab === 'listing' && (
              <>
                <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400">Platform Format</p>
                <div className="space-y-2">
                  {Object.entries(socialFormats).map(([key, fmt]) => {
                    const Icon = SOCIAL_ICONS[key] || Globe;
                    return (
                      <button key={key} onClick={() => setSelectedFormat(key)}
                        className={`w-full p-3 rounded-xl text-left border transition-all flex items-center gap-3 ${selectedFormat === key ? 'border-cyan-500 bg-cyan-500/10' : 'border-slate-700 bg-slate-800/50 hover:border-slate-500'}`}
                        data-testid={`format-${key}`}>
                        <Icon weight={selectedFormat === key ? 'fill' : 'regular'} className={`w-5 h-5 flex-shrink-0 ${selectedFormat === key ? 'text-cyan-400' : 'text-slate-500'}`} />
                        <div>
                          <p className={`text-sm font-semibold ${selectedFormat === key ? 'text-white' : 'text-slate-300'}`}>{fmt.label}</p>
                          <p className="text-[10px] text-slate-500">{fmt.width}x{fmt.height}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400 mb-2">Vehicle Info (optional)</p>
                  <textarea value={vehicleInfo} onChange={e => setVehicleInfo(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl p-3 text-sm text-white placeholder-slate-500 resize-none focus:border-cyan-500 focus:outline-none"
                    rows={3} placeholder="2021 BMW M5 — $49,999 — 32K Miles" data-testid="vehicle-info-input" />
                </div>
                <Button onClick={handleGenerateListing} disabled={processing || !selectedImage}
                  className="w-full bg-cyan-500 text-black font-bold hover:bg-cyan-400 shadow-[0_0_15px_rgba(0,240,255,0.4)] rounded-xl h-10 disabled:opacity-30"
                  data-testid="generate-listing-btn">
                  {processing ? <><Spinner weight="bold" className="w-4 h-4 mr-2 animate-spin" />Generating...</> : <><Lightning weight="bold" className="w-4 h-4 mr-2" />Generate Listing</>}
                </Button>
              </>
            )}

            {/* DAMAGE DETECTION */}
            {activeTab === 'damage' && (
              <>
                <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400">AI Damage Inspector</p>
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 space-y-3">
                  <ShieldCheck weight="duotone" className="w-8 h-8 text-cyan-400 mx-auto" />
                  <p className="text-sm text-white font-semibold text-center">Vehicle Damage Analysis</p>
                  <p className="text-xs text-slate-400 text-center">AI scans for scratches, dents, paint defects, and missing parts</p>
                  <ul className="text-[10px] text-slate-500 space-y-1">
                    <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-yellow-400" />Scratches (yellow)</li>
                    <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-500" />Dents (red)</li>
                    <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-orange-400" />Paint defects (orange)</li>
                    <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-purple-500" />Missing parts (purple)</li>
                  </ul>
                </div>
                <Button onClick={handleDamageDetect} disabled={processing || !selectedImage}
                  className="w-full bg-cyan-500 text-black font-bold hover:bg-cyan-400 shadow-[0_0_15px_rgba(0,240,255,0.4)] rounded-xl h-10 disabled:opacity-30"
                  data-testid="detect-damage-btn">
                  {processing ? <><Spinner weight="bold" className="w-4 h-4 mr-2 animate-spin" />Analyzing...</> : <><ShieldCheck weight="bold" className="w-4 h-4 mr-2" />Detect Damage</>}
                </Button>
              </>
            )}

            {/* VIDEO GENERATION */}
            {activeTab === 'video' && (
              <>
                <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400">AI Video Generator (Sora 2)</p>
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400 mb-2">Video Description</p>
                  <textarea value={videoPrompt} onChange={e => setVideoPrompt(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl p-3 text-sm text-white placeholder-slate-500 resize-none focus:border-cyan-500 focus:outline-none"
                    rows={4} placeholder="A cinematic slow-motion shot of a black BMW M5 driving through a city at night with neon reflections on wet streets..." data-testid="video-prompt-input" />
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400 mb-2">Duration</p>
                  <div className="flex gap-2">
                    {[4, 8, 12].map(d => (
                      <button key={d} onClick={() => setVideoDuration(d)}
                        className={`flex-1 py-2 rounded-xl text-xs font-bold border transition-all ${videoDuration === d ? 'border-cyan-500 bg-cyan-500/10 text-white' : 'border-slate-700 bg-slate-800/50 text-slate-400 hover:border-slate-500'}`}
                        data-testid={`video-duration-${d}`}>
                        {d}s
                      </button>
                    ))}
                  </div>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 text-xs text-amber-300">
                  <Lightning weight="fill" className="w-4 h-4 inline mr-1" />
                  Video generation takes 2-5 minutes. Please be patient.
                </div>
                <Button onClick={handleGenerateVideo} disabled={processing || !videoPrompt}
                  className="w-full bg-cyan-500 text-black font-bold hover:bg-cyan-400 shadow-[0_0_15px_rgba(0,240,255,0.4)] rounded-xl h-10 disabled:opacity-30"
                  data-testid="generate-video-btn">
                  {processing ? <><Spinner weight="bold" className="w-4 h-4 mr-2 animate-spin" />Generating Video...</> : <><Lightning weight="bold" className="w-4 h-4 mr-2" />Generate Video</>}
                </Button>
                {videoResult && (
                  <div className="space-y-2">
                    <video src={videoResult} controls className="w-full rounded-xl border border-white/10" data-testid="video-result" />
                    <a href={videoResult} download={`wakzy_video_${Date.now()}.mp4`} className="block">
                      <Button className="w-full bg-slate-800 text-white border border-slate-700 hover:bg-slate-700 rounded-xl text-xs h-8" data-testid="download-video-btn">
                        <DownloadSimple weight="bold" className="w-3.5 h-3.5 mr-1.5" />Download Video
                      </Button>
                    </a>
                  </div>
                )}
              </>
            )}


            {/* ANIMATED REELS */}
            {activeTab === 'reels' && (
              <>
                <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400">AI Car Reels (Sora 2)</p>
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400 mb-2">Vehicle</p>
                  <input value={reelVehicle} onChange={e => setReelVehicle(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl p-2.5 text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                    placeholder="2024 Black BMW M5 Competition" data-testid="reel-vehicle-input" />
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400 mb-2">Style</p>
                  <div className="space-y-1.5">
                    {reelStyles.map(s => (
                      <button key={s.id} onClick={() => setSelectedReelStyle(s.id)}
                        className={`w-full p-2.5 rounded-xl text-left text-xs border transition-all ${selectedReelStyle === s.id ? 'border-cyan-500 bg-cyan-500/10 text-white' : 'border-slate-700 bg-slate-800/50 text-slate-400 hover:border-slate-500'}`}
                        data-testid={`reel-style-${s.id}`}>
                        {s.name}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400 mb-1.5">Orientation</p>
                    <div className="flex gap-1.5">
                      {[['vertical', '9:16'], ['horizontal', '16:9']].map(([o, label]) => (
                        <button key={o} onClick={() => setReelOrientation(o)}
                          className={`flex-1 py-1.5 rounded-lg text-[10px] font-bold border ${reelOrientation === o ? 'border-cyan-500 bg-cyan-500/10 text-white' : 'border-slate-700 text-slate-400'}`}
                          data-testid={`reel-orient-${o}`}>{label}</button>
                      ))}
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400 mb-1.5">Duration</p>
                    <div className="flex gap-1.5">
                      {[4, 8, 12].map(d => (
                        <button key={d} onClick={() => setReelDuration(d)}
                          className={`flex-1 py-1.5 rounded-lg text-[10px] font-bold border ${reelDuration === d ? 'border-cyan-500 bg-cyan-500/10 text-white' : 'border-slate-700 text-slate-400'}`}
                          data-testid={`reel-dur-${d}`}>{d}s</button>
                      ))}
                    </div>
                  </div>
                </div>
                <Button onClick={handleGenerateReel} disabled={processing || !reelVehicle}
                  className="w-full bg-cyan-500 text-black font-bold hover:bg-cyan-400 shadow-[0_0_15px_rgba(0,240,255,0.4)] rounded-xl h-10 disabled:opacity-30"
                  data-testid="generate-reel-btn">
                  {processing ? <><Spinner weight="bold" className="w-4 h-4 mr-2 animate-spin" />Generating Reel...</> : <><FilmStrip weight="bold" className="w-4 h-4 mr-2" />Generate Reel</>}
                </Button>
                {reelResult && (
                  <div className="space-y-2">
                    <video src={reelResult} controls className="w-full rounded-xl border border-white/10" data-testid="reel-result" />
                    <a href={reelResult} download={`wakzy_reel_${Date.now()}.mp4`} className="block">
                      <Button className="w-full bg-slate-800 text-white border border-slate-700 hover:bg-slate-700 rounded-xl text-xs h-8">
                        <DownloadSimple weight="bold" className="w-3.5 h-3.5 mr-1.5" />Download Reel
                      </Button>
                    </a>
                  </div>
                )}
              </>
            )}

            {/* VOICE-OVER COMMERCIALS */}
            {activeTab === 'voiceover' && (
              <>
                <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400">AI Voice-Over Commercial</p>
                <div className="space-y-3">
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 mb-1">Vehicle</p>
                    <input value={voVehicle} onChange={e => setVoVehicle(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-xl p-2 text-xs text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                      placeholder="2024 Honda Civic EX" data-testid="vo-vehicle" />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 mb-1">Dealership Name</p>
                    <input value={voDealerName} onChange={e => setVoDealerName(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-xl p-2 text-xs text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                      placeholder="Wakzy Auto Tech" data-testid="vo-dealer" />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 mb-1">Key Features</p>
                    <input value={voFeatures} onChange={e => setVoFeatures(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-xl p-2 text-xs text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                      placeholder="low miles, clean title, one owner" data-testid="vo-features" />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 mb-1">Price (optional)</p>
                    <input value={voPrice} onChange={e => setVoPrice(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-xl p-2 text-xs text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                      placeholder="$22,499" data-testid="vo-price" />
                  </div>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400 mb-1.5">Template</p>
                  <div className="flex gap-1.5 flex-wrap">
                    {[['dealership', 'Promo'], ['luxury', 'Luxury'], ['value', 'Value'], ['custom', 'Custom']].map(([id, label]) => (
                      <button key={id} onClick={() => setVoTemplate(id)}
                        className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border ${voTemplate === id ? 'border-cyan-500 bg-cyan-500/10 text-white' : 'border-slate-700 text-slate-400'}`}
                        data-testid={`vo-template-${id}`}>{label}</button>
                    ))}
                  </div>
                </div>
                <Button onClick={handleGenerateScript} variant="outline" className="w-full border-slate-700 text-slate-300 rounded-xl text-xs h-8" data-testid="generate-script-btn">
                  Generate Script
                </Button>
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400 mb-1.5">Script</p>
                  <textarea value={voScript} onChange={e => setVoScript(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl p-2.5 text-xs text-white placeholder-slate-500 resize-none focus:border-cyan-500 focus:outline-none"
                    rows={4} placeholder="Your commercial script..." data-testid="vo-script" />
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400 mb-1.5">Voice</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {voiceOptions.map(v => (
                      <button key={v.id} onClick={() => setSelectedVoice(v.id)}
                        className={`p-2 rounded-xl text-left border transition-all ${selectedVoice === v.id ? 'border-cyan-500 bg-cyan-500/10' : 'border-slate-700 bg-slate-800/50 hover:border-slate-500'}`}
                        data-testid={`voice-${v.id}`}>
                        <p className={`text-[10px] font-bold ${selectedVoice === v.id ? 'text-white' : 'text-slate-300'}`}>{v.name}</p>
                        <p className="text-[9px] text-slate-500">{v.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
                <Button onClick={handleGenerateVoiceover} disabled={processing || !voScript}
                  className="w-full bg-cyan-500 text-black font-bold hover:bg-cyan-400 shadow-[0_0_15px_rgba(0,240,255,0.4)] rounded-xl h-10 disabled:opacity-30"
                  data-testid="generate-voiceover-btn">
                  {processing ? <><Spinner weight="bold" className="w-4 h-4 mr-2 animate-spin" />Generating...</> : <><Microphone weight="bold" className="w-4 h-4 mr-2" />Generate Voice-Over</>}
                </Button>
                {audioResult && (
                  <div className="space-y-2 bg-slate-800/50 border border-slate-700 rounded-xl p-3">
                    <audio src={audioResult} controls className="w-full" data-testid="audio-result" />
                    <a href={audioResult} download={`wakzy_voiceover_${Date.now()}.mp3`} className="block">
                      <Button className="w-full bg-slate-800 text-white border border-slate-700 hover:bg-slate-700 rounded-xl text-xs h-8">
                        <DownloadSimple weight="bold" className="w-3.5 h-3.5 mr-1.5" />Download MP3
                      </Button>
                    </a>
                  </div>
                )}
              </>
            )}

            {/* 360° VIEWER TAB */}
            {activeTab === '360' && (
              <>
                <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400">360° Vehicle Viewer</p>
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 space-y-3">
                  <CubeTransparent weight="duotone" className="w-8 h-8 text-cyan-400 mx-auto" />
                  <p className="text-sm text-white font-semibold text-center">Interactive Spin Viewer</p>
                  <p className="text-xs text-slate-400 text-center">Upload photos from multiple angles to create an interactive 360° presentation</p>
                  <ul className="text-[10px] text-slate-500 space-y-1.5">
                    <li className="flex items-center gap-2"><CheckCircle weight="fill" className="w-3 h-3 text-cyan-500" />Drag to rotate</li>
                    <li className="flex items-center gap-2"><CheckCircle weight="fill" className="w-3 h-3 text-cyan-500" />Auto-play spin</li>
                    <li className="flex items-center gap-2"><CheckCircle weight="fill" className="w-3 h-3 text-cyan-500" />8-24 angles recommended</li>
                    <li className="flex items-center gap-2"><CheckCircle weight="fill" className="w-3 h-3 text-cyan-500" />Mobile responsive</li>
                  </ul>
                </div>
                <p className="text-[10px] text-slate-500 text-center">Use the main canvas to manage your 360° viewers</p>
              </>
            )}

          </div>
        </div>
      </div>

      {/* Plan Selection Modal */}
      {showPlanModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="plan-modal">
          <div className="bg-[#0A101D] border border-white/10 rounded-2xl max-w-3xl w-full overflow-hidden shadow-2xl">
            <div className="p-6 border-b border-white/5 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">Choose Your Image Pro Plan</h2>
                <p className="text-sm text-slate-400 mt-1">AI-powered vehicle photography tools</p>
              </div>
              <button onClick={() => setShowPlanModal(false)} className="text-slate-400 hover:text-white"><X weight="bold" className="w-5 h-5" /></button>
            </div>
            <div className="p-6 grid md:grid-cols-3 gap-4">
              {plans.map(plan => {
                const isCurrent = myPlan?.plan_id === plan.id;
                const isPopular = plan.id === 'image_professional';
                return (
                  <div key={plan.id} className={`relative rounded-xl border p-5 flex flex-col ${isCurrent ? 'border-cyan-500 bg-cyan-500/5' : isPopular ? 'border-cyan-500/50 bg-slate-800/80' : 'border-slate-700 bg-slate-800/50'}`} data-testid={`plan-card-${plan.id}`}>
                    {isPopular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] tracking-wider uppercase font-bold px-3 py-1 rounded-full bg-cyan-500 text-black">Most Popular</span>}
                    <h3 className="text-lg font-bold text-white">{plan.name}</h3>
                    <div className="flex items-baseline gap-1 mt-2">
                      <span className="text-3xl font-black text-white">${plan.price}</span>
                      <span className="text-sm text-slate-400">/mo</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">{plan.monthly_edits === -1 ? 'Unlimited edits' : `${plan.monthly_edits} AI edits/month`}</p>
                    <ul className="mt-4 space-y-2 flex-1">
                      {plan.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-slate-300">
                          <CheckCircle weight="fill" className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0 mt-0.5" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    {isCurrent ? (
                      <Button variant="outline" className="w-full mt-4 rounded-xl border-slate-600 text-slate-400 text-xs" onClick={handleCancelPlan} data-testid={`cancel-plan-${plan.id}`}>
                        Current Plan — Cancel
                      </Button>
                    ) : (
                      <Button className={`w-full mt-4 rounded-xl text-xs font-bold ${isPopular ? 'bg-cyan-500 text-black hover:bg-cyan-400 shadow-[0_0_15px_rgba(0,240,255,0.4)]' : 'bg-slate-700 text-white hover:bg-slate-600'}`}
                        onClick={() => handleSubscribePlan(plan.id)} data-testid={`subscribe-plan-${plan.id}`}>
                        {myPlan?.plan_id ? 'Switch Plan' : 'Activate'}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
